import { heroIcons } from "../ui/icons";
import { motion } from "framer-motion";
import LinkButton from "../ui/LinkButton";

const TECH = [
  {
    name: "React Native",
    url: "https://reactnative.dev/",
    icon: heroIcons.react,
  },
  {
    name: "Electron",
    url: "https://www.electronjs.org/",
    icon: heroIcons.electron,
  },
  {
    name: "Node.js",
    url: "https://nodejs.org/",
    icon: heroIcons.nodejs,
  },
  {
    name: "MySQL",
    url: "https://www.mysql.com/",
    icon: heroIcons.mysql,
  },
  {
    name: "Tailwind CSS",
    url: "https://tailwindcss.com/",
    icon: heroIcons.tailwind,
  },
  {
    name: "Docker",
    url: "https://www.docker.com/",
    icon: heroIcons.docker,
  },
];

const SOCIALS = [
  {
    name: "GitHub",
    url: "https://github.com/cherubinifranco",
  },
  {
    name: "LinkedIn",
    url: "https://www.linkedin.com/in/cherubinifranco/",
  },
];

export default function Hero() {
  return (
    <section
      id="top"
      className="relative min-h-screen overflow-hidden bg-[#010710] text-white"
    >
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />
      <div className="relative mx-auto flex min-h-screen max-w-6xl items-center px-6 pb-20 pt-32 sm:pt-36">
        <div className="max-w-4xl">
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.1,
              duration: 0.7,
              ease: "easeOut",
            }}
            className="max-w-4xl text-5xl font-medium leading-[1.05] tracking-[-0.04em] sm:text-6xl md:text-7xl lg:text-[5.5rem]"
          >
            I build software
            <span className="block">people actually use.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.25,
              duration: 0.7,
            }}
            className="mt-7 max-w-2xl text-base leading-7 text-white/45 sm:text-lg"
          >
            Software developer focused on backend and full-stack development,
            building reliable products across web, desktop, and mobile.
            Bilingual in English and Spanish.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.4,
              duration: 0.7,
            }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <LinkButton url="/#projects">
              View my work <span className="ml-1">↗</span>
            </LinkButton>

            <a
              href="Resume.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-5 py-2.5 text-sm font-medium text-white/70 backdrop-blur-sm transition hover:border-white/20 hover:bg-white/[0.07] hover:text-white"
            >
              Download resume
              <span className="text-white/30 transition-transform group-hover:translate-y-0.5">
                ↓
              </span>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              delay: 0.55,
              duration: 0.7,
            }}
            className="mt-7 flex items-center gap-5"
          >
            {SOCIALS.map((social, index) => (
              <span key={social.name} className="flex items-center">
                <a
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-1.5 text-sm text-white/35 transition hover:text-white"
                >
                  {social.name}
                  <span className="text-white/20 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5">
                    ↗
                  </span>
                </a>

                {index < SOCIALS.length - 1 && (
                  <span className="ml-5 h-1 w-1 rounded-full bg-white/15" />
                )}
              </span>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.65,
              duration: 0.7,
            }}
            className="mt-12 flex items-center gap-8"
          >
            <div>
              <p className="text-2xl font-medium tracking-tight text-white">
                20+
              </p>
              <p className="mt-1 text-xs uppercase tracking-[0.16em] text-white/25">
                Projects shipped
              </p>
            </div>

            <div className="h-8 w-px bg-white/[0.08]" />

            <div>
              <p className="text-2xl font-medium tracking-tight text-white">
                3+
              </p>
              <p className="mt-1 text-xs uppercase tracking-[0.16em] text-white/25">
                Years of experience
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.75,
              duration: 0.7,
            }}
            className="mt-16 border-t border-white/[0.07] pt-6"
          >
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-4">
                {TECH.map((tech, index) => (
                  <motion.a
                    key={tech.name}
                    href={tech.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={tech.name}
                    aria-label={tech.name}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                      delay: 0.8 + index * 0.06,
                      duration: 0.35,
                    }}
                    className="flex h-9 w-9 items-center justify-center text-white/35 transition duration-200 hover:text-white"
                  >
                    {tech.icon}
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
